import { useEffect } from 'react';
import { Button } from 'react-aria-components';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import JaneLogoImg from '~/assets/images/jane_logo_200.svg';
import { LoadingView } from '~/components';
import { useAppDispatch, useAppSelector } from '~/state';
import {
  login,
  selectAuthError,
  selectIsAuthenticated,
  selectIsAuthenticating,
} from '~/state/auth';

type FormValues = {
  password: string;
};

export function Login() {
  const authenticated = useAppSelector(selectIsAuthenticated);
  const isAuthenticating = useAppSelector(selectIsAuthenticating);
  const authError = useAppSelector(selectAuthError);
  const hasError = !!authError;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { register, handleSubmit } = useForm<FormValues>();

  useEffect(() => {
    if (authenticated) {
      navigate('/');
    }
  }, [navigate, authenticated]);

  const submitForm = (data: FormValues) => dispatch(login(data.password));

  return (
    <>
      <div
        data-testid="login-page"
        className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8"
      >
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          {!isAuthenticating && (
            <img
              className="mx-auto h-20 w-auto"
              src={JaneLogoImg}
              alt="Jane logo"
            />
          )}
          {isAuthenticating && (
            <div className="mx-auto -mt-4 h-24 w-20">
              <LoadingView loading={true} noMessage size="xl" />
            </div>
          )}
          <h2 className="mt-8 text-center text-2xl font-bold leading-9 tracking-tight text-neutral-90">
            Jane Discovery Alpha
          </h2>
          <span className="mt-2 text-center text-sm text-neutral-60">
            <p>Hello Jane-er 👋!</p>
            <p>
              To access the Alpha, please use the &apos;<b>Marketplace Alpha</b>
              &apos; password in our 1Password Shared Vault.
            </p>
          </span>
        </div>

        <div className="mt-4 sm:mx-auto sm:w-full sm:max-w-sm">
          <form className="space-y-6" onSubmit={handleSubmit(submitForm)}>
            <div>
              <div className="mt-2">
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  required
                  disabled={isAuthenticating}
                  className={`focus-ring block w-full rounded-md p-2 outline outline-1 sm:text-sm sm:leading-6 ${hasError ? 'outline-critical-50' : 'outline-neutral-40'}`}
                  {...register('password')}
                />
                {hasError && (
                  <p className="p-1 text-center text-sm text-critical-50">
                    {authError}
                  </p>
                )}
              </div>
            </div>
            <div>
              <Button
                type="submit"
                isDisabled={isAuthenticating}
                className="flex w-full justify-center rounded-md bg-brand-60 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-brand-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:bg-brand-10"
              >
                Sign in
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
